import { QuoteFeedExtended } from './feed';
import {
  DetailedQuote,
  ShortInterestParams,
  ShortInterestsDataSet,
  Logo,
  SymbolType,
  DelayedQuote,
  TickerDetail,
  GetQuotesLogoParams,
  PeerQuotesResponse,
} from './entities';
import { StockSymbol } from '@benzinga/session';

interface CachedDelayedQuote {
  cachedAt: number;
  quote: DelayedQuote;
}

interface CachedPeerQuotes {
  cachedAt: number;
  response: PeerQuotesResponse;
}

export const getPeerQuotesCacheKey = (symbol: StockSymbol, limit?: number): string => `${symbol}:${limit ?? 'all'}`;

export const REJECTED_LOGO_TTL_MS = 5 * 60 * 1000;
export const LOGO_CACHE_MAX_AGE_MS = 48 * 60 * 60 * 1000;

const LOGO_TOKEN_VALID_FOR_SEC = 24 * 60 * 60;

interface CachedLogo {
  cachedAt: number;
  logo: Logo;
}

function expiryUnixSecFromLogoUrl(url: string): number | null {
  const legacyMatch = url.match(/(?:[?&])x-bz-exp=(\d+)/);
  if (legacyMatch) {
    const legacyExpiry = parseInt(legacyMatch[1], 10);
    if (!Number.isNaN(legacyExpiry)) {
      return legacyExpiry;
    }
  }

  const tokenMatch = url.match(/(?:[?&])token=(\d+)-/);
  if (!tokenMatch) {
    return null;
  }
  const tokenTimestamp = parseInt(tokenMatch[1], 10);
  if (Number.isNaN(tokenTimestamp)) {
    return null;
  }
  return tokenTimestamp + LOGO_TOKEN_VALID_FOR_SEC;
}

function getLogoSignedUrlExpiryUnixSec(logo: Logo): number | null {
  const files = logo?.files;
  if (!files || typeof files !== 'object') {
    return null;
  }
  let earliestExpiry: number | null = null;
  for (const url of Object.values(files)) {
    if (typeof url !== 'string' || !url) {
      continue;
    }
    const expiry = expiryUnixSecFromLogoUrl(url);
    if (expiry === null) {
      continue;
    }
    earliestExpiry = earliestExpiry === null ? expiry : Math.min(earliestExpiry, expiry);
  }
  return earliestExpiry;
}

export class QuoteStore {
  private static readonly DELAYED_QUOTE_TTL_MS = 60000;
  private static readonly PEER_QUOTES_TTL_MS = 60000;

  private quoteFeeds = new Map<SymbolType, QuoteFeedExtended>();
  private activeQuoteFeeds = new Set<QuoteFeedExtended>();
  private detailedQuotes = new Map<SymbolType, DetailedQuote>();
  private delayedQuotes = new Map<SymbolType, CachedDelayedQuote>();
  private quotesLogos = new Map<string | SymbolType, CachedLogo>();
  private tickerDetails = new Map<StockSymbol, TickerDetail>();
  private shortInterestQuotes = new WeakMap<ShortInterestParams, ShortInterestsDataSet>();
  private peerQuotes = new Map<string, CachedPeerQuotes>();

  public addQuoteFeed = (quoteFeed: QuoteFeedExtended): QuoteFeedExtended => {
    this.quoteFeeds.set(quoteFeed.getSecurities(), quoteFeed);
    return quoteFeed;
  };

  public removeQuoteFeed = (symbol: SymbolType): void => {
    this.quoteFeeds.delete(symbol);
  };

  public addActiveQuoteFeed = (quoteFeed: QuoteFeedExtended): QuoteFeedExtended => {
    this.activeQuoteFeeds.add(quoteFeed);
    return quoteFeed;
  };

  public removeActiveQuoteFeed = (quoteFeed: QuoteFeedExtended): void => {
    this.activeQuoteFeeds.delete(quoteFeed);
  };

  public getQuoteFeeds = (): QuoteFeedExtended[] => {
    return Array.from(this.quoteFeeds.values());
  };

  public getActiveQuoteFeeds = (): QuoteFeedExtended[] => {
    return Array.from(this.activeQuoteFeeds.values());
  };

  public hasQuoteFeed = (symbol: SymbolType): boolean => {
    return this.quoteFeeds.has(symbol);
  };

  public getQuoteFeed = (symbol: SymbolType): QuoteFeedExtended | undefined => {
    return this.quoteFeeds.get(symbol);
  };

  public getDetailedQuotes = (symbol: SymbolType): DetailedQuote | undefined => {
    return this.detailedQuotes.get(symbol);
  };

  public setDetailedQuotes = (symbol: SymbolType, quote: DetailedQuote): void => {
    this.detailedQuotes.set(symbol, quote);
  };

  public getDelayedQuotes = (): DelayedQuote[] => {
    const freshQuotes: DelayedQuote[] = [];
    const now = Date.now();

    this.delayedQuotes.forEach((cached, symbol) => {
      if (now - cached.cachedAt < QuoteStore.DELAYED_QUOTE_TTL_MS) {
        freshQuotes.push(cached.quote);
      } else {
        this.delayedQuotes.delete(symbol);
      }
    });

    return freshQuotes;
  };

  public getDelayedQuote = (symbol: SymbolType): DelayedQuote | undefined => {
    const cached = this.delayedQuotes.get(symbol);
    if (!cached) {
      return undefined;
    }

    if (Date.now() - cached.cachedAt > QuoteStore.DELAYED_QUOTE_TTL_MS) {
      this.delayedQuotes.delete(symbol);
      return undefined;
    }

    return cached.quote;
  };

  public setDelayedQuotes = (symbol: SymbolType, quote: DelayedQuote): void => {
    this.delayedQuotes.set(symbol, { cachedAt: Date.now(), quote });
  };

  public shouldFetchDelayedQuotes = (symbols: SymbolType[]): SymbolType[] => {
    const now = Date.now();
    return symbols.filter(symbol => {
      const cached = this.delayedQuotes.get(symbol);
      return !cached || now - cached.cachedAt > QuoteStore.DELAYED_QUOTE_TTL_MS;
    });
  };

  public addQuotesLogos = (symbol: SymbolType, params: GetQuotesLogoParams | undefined, logo: Logo): void => {
    const cached = { cachedAt: Date.now(), logo };
    if (params === undefined) {
      this.quotesLogos.set(symbol, cached);
    } else {
      this.quotesLogos.set(JSON.stringify({ ...params, symbol }), cached);
    }
  };

  public getQuotesLogos = (symbol: SymbolType, params: GetQuotesLogoParams | undefined): Logo | undefined => {
    const cacheKey = params === undefined ? symbol : JSON.stringify({ ...params, symbol });
    const cached = this.quotesLogos.get(cacheKey);
    if (!cached) {
      return undefined;
    }
    const urlExpSec = getLogoSignedUrlExpiryUnixSec(cached.logo);
    const nowMs = Date.now();
    const residenceExpired = nowMs - cached.cachedAt >= LOGO_CACHE_MAX_AGE_MS;
    const urlExpired = urlExpSec !== null && Math.floor(nowMs / 1000) >= urlExpSec;
    const unrecognizedExpired = urlExpSec === null && nowMs - cached.cachedAt >= REJECTED_LOGO_TTL_MS;
    if (residenceExpired || urlExpired || unrecognizedExpired) {
      this.quotesLogos.delete(cacheKey);
      return undefined;
    }
    return cached.logo;
  };

  public getShortInterest = (params: ShortInterestParams): ShortInterestsDataSet | undefined => {
    return this.shortInterestQuotes.get(params);
  };

  public setShortInterest = (params: ShortInterestParams, quotes: ShortInterestsDataSet): void => {
    this.shortInterestQuotes.set(params, quotes);
  };

  public getTickerDetails = (symbol: StockSymbol): TickerDetail | undefined => {
    return this.tickerDetails.get(symbol);
  };

  public setTickerDetails = (details: TickerDetail): void => {
    this.tickerDetails.set(details.symbol, details);
  };

  public getPeerQuotes = (cacheKey: string): PeerQuotesResponse | undefined => {
    const cached = this.peerQuotes.get(cacheKey);
    if (!cached) {
      return undefined;
    }

    if (Date.now() - cached.cachedAt > QuoteStore.PEER_QUOTES_TTL_MS) {
      this.peerQuotes.delete(cacheKey);
      return undefined;
    }

    return cached.response;
  };

  public setPeerQuotes = (cacheKey: string, response: PeerQuotesResponse): void => {
    this.peerQuotes.set(cacheKey, { cachedAt: Date.now(), response });
  };
}
